import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Search, SlidersHorizontal, X, ArrowRight, Phone, Home as HomeIcon, ShieldCheck, Accessibility } from "lucide-react";
import { api } from "../lib/api";
import VacancyCard from "../components/VacancyCard";
import VideoSection from "../components/VideoSection";
import FeaturedHome from "../components/FeaturedHome";
import PropertyListingCard from "../components/PropertyListingCard";
import VacancyAlertForm from "../components/VacancyAlertForm";
import { PROPERTIES } from "../data/properties";
import { useProperties } from "../lib/useProperties";
import {
  SUPPORT_RATIOS as SUPPORT_RATIO_OPTS,
  SUPPORT_MODELS as SUPPORT_MODEL_OPTS,
  SUITABILITY_TAGS as SUITABILITY_OPTS,
  matchesFilters,
} from "../data/propertyModel";
import { VIDEOS } from "../data/videos";
import { useSEO, breadcrumbSchema } from "../lib/seo";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Input } from "../components/ui/input";
import { Checkbox } from "../components/ui/checkbox";
import { Sheet, SheetContent, SheetTrigger } from "../components/ui/sheet";
import { trackPhoneClick } from "../lib/tracking";

// Accessibility toggle-filters — canonical keys map to propertyModel.ACCESSIBILITY_TAGS values
// (the top-of-panel most-used quick filters for participants using assistive tech)
const ACCESSIBILITY_QUICK = [
  { key: "wheelchair-accessible", label: "Wheelchair accessible" },
  { key: "step-free-entry", label: "Step-free entry" },
  { key: "single-storey", label: "Single-storey" },
  { key: "accessible-bathroom", label: "Accessible bathroom" },
  { key: "accessible-kitchen", label: "Accessible kitchen" },
  { key: "ceiling-hoist", label: "Ceiling hoist" },
  { key: "lift-access", label: "Lift access" },
  { key: "wide-doorways", label: "Wide doorways" },
  { key: "accessible-parking", label: "Accessible parking" },
];

const SDA_CATEGORY_OPTS = [
  { value: "improved-liveability", label: "Improved Liveability" },
  { value: "fully-accessible", label: "Fully Accessible" },
  { value: "robust", label: "Robust" },
  { value: "high-physical-support", label: "High Physical Support" },
];

const STATE_OPTS = ["VIC", "NSW", "QLD", "WA", "SA", "ACT", "TAS", "NT"];

const BEDROOM_OPTS = [
  { v: "", label: "Any" },
  { v: "1", label: "1+" },
  { v: "2", label: "2+" },
  { v: "3", label: "3+" },
  { v: "4", label: "4+" },
];

function Filters({ facets, filters, setFilters, clear }) {
  const setK = (k) => (v) => setFilters((f) => ({ ...f, [k]: v === "any" || v === "" ? "" : v }));
  const toggleAccess = (feat) => {
    const cur = new Set(filters.accessibility);
    if (cur.has(feat)) cur.delete(feat); else cur.add(feat);
    setFilters((f) => ({ ...f, accessibility: [...cur] }));
  };
  return (
    <div className="space-y-6" data-testid="vacancy-filters">
      <div>
        <label className="alpha-overline">Search</label>
        <div className="relative mt-2">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[color:var(--alpha-mute)]" />
          <Input
            value={filters.q}
            onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
            placeholder="Suburb, title, keyword"
            className="pl-9 h-11"
            data-testid="filter-search"
          />
        </div>
      </div>

      <div>
        <label className="alpha-overline">State</label>
        <Select value={filters.state || "any"} onValueChange={setK("state")}>
          <SelectTrigger className="mt-2 h-11" data-testid="filter-state"><SelectValue placeholder="Any state" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="any">Any state</SelectItem>
            {STATE_OPTS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div>
        <label className="alpha-overline">Suburb</label>
        <Input
          className="mt-2 h-11"
          value={filters.suburb}
          onChange={(e) => setFilters((f) => ({ ...f, suburb: e.target.value }))}
          placeholder="e.g. Parramatta"
          data-testid="filter-suburb"
        />
      </div>

      <div>
        <label className="alpha-overline">Service type</label>
        <Select value={filters.service_type || "any"} onValueChange={setK("service_type")}>
          <SelectTrigger className="mt-2 h-11" data-testid="filter-service"><SelectValue placeholder="Any type" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="any">Any type</SelectItem>
            {(facets?.service_types || []).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div>
        <label className="alpha-overline">SDA design category</label>
        <Select value={filters.sda_category || "any"} onValueChange={setK("sda_category")}>
          <SelectTrigger className="mt-2 h-11" data-testid="filter-sda-category"><SelectValue placeholder="Any SDA category" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="any">Any SDA category</SelectItem>
            {SDA_CATEGORY_OPTS.map((s) => (
              <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="mt-1.5 text-[11px] text-[color:var(--alpha-mute)]">Only applies to SDA listings.</p>
      </div>

      <div>
        <label className="alpha-overline">Bedrooms</label>
        <div className="mt-2 flex flex-wrap gap-2">
          {BEDROOM_OPTS.map((b) => (
            <button
              key={b.label}
              onClick={() => setFilters((f) => ({ ...f, bedrooms: b.v }))}
              className={`alpha-chip cursor-pointer ${filters.bedrooms === b.v ? "!bg-[color:var(--alpha-purple-800)] !text-white !border-[color:var(--alpha-purple-800)]" : ""}`}
              data-testid={`filter-bed-${b.label}`}
            >
              {b.label}
            </button>
          ))}
        </div>
      </div>

      <div>
        <label className="alpha-overline">Availability</label>
        <Select value={filters.availability || "any"} onValueChange={setK("availability")}>
          <SelectTrigger className="mt-2 h-11" data-testid="filter-avail"><SelectValue placeholder="Any" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="any">Any</SelectItem>
            <SelectItem value="available">Available now</SelectItem>
            <SelectItem value="limited">Limited rooms</SelectItem>
            <SelectItem value="waitlist">Join waitlist</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div>
        <label className="alpha-overline inline-flex items-center gap-1.5"><Accessibility className="w-3.5 h-3.5" /> Accessibility features</label>
        <div className="mt-3 space-y-2">
          {ACCESSIBILITY_QUICK.map((f) => (
            <label key={f.key} className="flex items-center gap-3 text-sm cursor-pointer">
              <Checkbox
                checked={filters.accessibility.includes(f.key)}
                onCheckedChange={() => toggleAccess(f.key)}
                data-testid={`filter-access-${f.key}`}
              />
              <span>{f.label}</span>
            </label>
          ))}
        </div>
      </div>

      <div>
        <label className="alpha-overline">Support ratio</label>
        <div className="mt-2 flex flex-wrap gap-2">
          {SUPPORT_RATIO_OPTS.map((r) => (
            <button
              key={r.value}
              onClick={() => {
                const cur = new Set(filters.support_ratios || []);
                if (cur.has(r.value)) cur.delete(r.value); else cur.add(r.value);
                setFilters((f) => ({ ...f, support_ratios: [...cur] }));
              }}
              className={`alpha-chip cursor-pointer ${(filters.support_ratios || []).includes(r.value) ? "!bg-[color:var(--alpha-purple-800)] !text-white !border-[color:var(--alpha-purple-800)]" : ""}`}
              data-testid={`filter-ratio-${r.value.replace(":", "-")}`}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      <div>
        <label className="alpha-overline">Support model</label>
        <div className="mt-2 flex flex-wrap gap-2">
          {SUPPORT_MODEL_OPTS.map((m) => (
            <button
              key={m.value}
              onClick={() => {
                const cur = new Set(filters.support_model || []);
                if (cur.has(m.value)) cur.delete(m.value); else cur.add(m.value);
                setFilters((f) => ({ ...f, support_model: [...cur] }));
              }}
              className={`alpha-chip cursor-pointer ${(filters.support_model || []).includes(m.value) ? "!bg-[color:var(--alpha-purple-800)] !text-white !border-[color:var(--alpha-purple-800)]" : ""}`}
              data-testid={`filter-model-${m.value}`}
            >
              {m.label}
            </button>
          ))}
        </div>
      </div>

      <div>
        <label className="alpha-overline">Suitability</label>
        <p className="text-[11px] text-[color:var(--alpha-mute)] mt-1 mb-2">For referral discovery — not clinical eligibility.</p>
        <div className="flex flex-wrap gap-2">
          {SUITABILITY_OPTS.map((s) => (
            <button
              key={s.value}
              onClick={() => {
                const cur = new Set(filters.suitability || []);
                if (cur.has(s.value)) cur.delete(s.value); else cur.add(s.value);
                setFilters((f) => ({ ...f, suitability: [...cur] }));
              }}
              className={`alpha-chip cursor-pointer !text-[11px] ${(filters.suitability || []).includes(s.value) ? "!bg-[color:var(--alpha-purple-800)] !text-white !border-[color:var(--alpha-purple-800)]" : ""}`}
              data-testid={`filter-suitability-${s.value}`}
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>

      <button onClick={clear} className="text-sm font-semibold text-[color:var(--alpha-purple-700)] inline-flex items-center gap-1" data-testid="filter-clear">
        <X className="w-3.5 h-3.5" /> Clear all filters
      </button>
    </div>
  );
}

export default function VacanciesPage() {
  useSEO({
    title: "SIL & SDA Vacancies Melbourne · Victoria & Perth | Alpha Community Care",
    description: "Browse accessible SIL, SDA, STA and MTA vacancies across Melbourne, Victoria and Perth. Filter by state, suburb, service type, SDA design category and accessibility features. 1:1 and 1:2 support only.",
    keywords: [
      "SIL vacancies Melbourne", "SIL vacancies Victoria", "SIL vacancies Perth",
      "SDA vacancies Melbourne", "SDA Victoria", "SDA Perth",
      "NDIS housing Melbourne", "NDIS housing Victoria",
      "Specialist Disability Accommodation Melbourne",
      "Supported Independent Living Melbourne",
      "NDIS vacancy", "NDIS provider Melbourne",
    ],
    canonical: "https://alphacommunitycare.com.au/vacancies",
    jsonLd: [breadcrumbSchema([
      { name: "Home", path: "/" },
      { name: "Our Homes", path: "/vacancies" },
    ])],
  });
  const [sp, setSp] = useSearchParams();
  const [tab, setTab] = useState(sp.get("tab") || "sil"); // SIL default per brief
  const [filters, setFilters] = useState({
    q: sp.get("q") || "",
    state: sp.get("state") || "",
    suburb: sp.get("suburb") || "",
    service_type: sp.get("service_type") || "",
    sda_category: sp.get("sda_category") || "",
    bedrooms: sp.get("bedrooms") || "",
    availability: sp.get("availability") || "",
    accessibility: sp.get("accessibility") ? sp.get("accessibility").split(",") : [],
    support_ratios: sp.get("support_ratios") ? sp.get("support_ratios").split(",") : [],
    support_model: sp.get("support_model") ? sp.get("support_model").split(",") : [],
    suitability: sp.get("suitability") ? sp.get("suitability").split(",") : [],
  });
  const [items, setItems] = useState([]);
  const [facets, setFacets] = useState(null);
  const [loading, setLoading] = useState(true);

  const qs = useMemo(() => {
    const p = {};
    Object.entries(filters).forEach(([k, v]) => {
      if (Array.isArray(v) ? v.length : v) p[k] = Array.isArray(v) ? v.join(",") : v;
    });
    if (tab && tab !== "all") p.tab = tab;
    return p;
  }, [filters, tab]);

  useEffect(() => {
    const t = setTimeout(() => {
      setSp(qs, { replace: true });
      setLoading(true);
      api
        .get("/vacancies", { params: qs })
        .then((r) => {
          setItems(r.data.items || []);
          setFacets(r.data.facets);
        })
        .finally(() => setLoading(false));
    }, 200);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(qs)]);

  const clear = () => setFilters({ q: "", state: "", suburb: "", service_type: "", sda_category: "", bedrooms: "", availability: "", accessibility: [], support_ratios: [], support_model: [], suitability: [] });

  const { properties: LIVE_PROPERTIES } = useProperties();

  // Structured properties (verified + demo) — filter fully client-side using canonical keys.
  const filteredProps = useMemo(() => {
    return LIVE_PROPERTIES.filter((p) => {
      // Tab classification
      if (tab === "sil" && p.sda_category) return false;
      if (tab === "sda" && !p.sda_category) return false;
      // Structured filters
      if (!matchesFilters(p, {
        state: filters.state || undefined,
        suburb: filters.suburb || undefined,
        support_ratios: filters.support_ratios,
        support_model: filters.support_model,
        suitability: filters.suitability,
        accessibility: filters.accessibility,
        sda_categories: filters.sda_category ? [filters.sda_category] : undefined,
      })) return false;
      // Bedrooms threshold
      if (filters.bedrooms && (p.participant_bedrooms || p.total_bedrooms || 0) < parseInt(filters.bedrooms, 10)) return false;
      // Free text search
      if (filters.q) {
        const q = filters.q.toLowerCase();
        const hay = `${p.name} ${p.suburb} ${p.state} ${p.oneLiner || ""} ${(p.features || []).join(" ")}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [LIVE_PROPERTIES, filters, tab]);

  // API vacancies rendered only when tab is "all" or "stamta" (backend has no property_type awareness)
  const showApiItems = tab === "all" || tab === "stamta";

  return (
    <div data-testid="page-vacancies">
      {/* Cinematic dark hero */}
      <section className="relative overflow-hidden text-white">
        <div className="absolute inset-0">
          <img
            src={PROPERTIES[0].images[0].src}
            alt=""
            aria-hidden
            className="absolute inset-0 w-full h-full object-cover alpha-ken-burns"
            loading="eager"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-[#0F0625]/55 via-[#0F0625]/70 to-[#0F0625]/95" />
        <div className="absolute inset-0 bg-gradient-to-tr from-[#1B0B3D]/65 via-transparent to-[#4FA56E]/10 mix-blend-multiply" />
        <div className="alpha-flow-lines" aria-hidden />
        <div className="alpha-particles" aria-hidden />
        <div className="alpha-grain-dark" aria-hidden />
        <div className="absolute -left-20 top-10 w-[420px] h-[420px] rounded-full pointer-events-none alpha-glow-a" style={{ background: "radial-gradient(closest-side, rgba(134,89,199,0.55), transparent 70%)", filter: "blur(50px)" }} aria-hidden />
        <div className="absolute -right-20 bottom-10 w-[420px] h-[420px] rounded-full pointer-events-none alpha-glow-b" style={{ background: "radial-gradient(closest-side, rgba(79,165,110,0.45), transparent 70%)", filter: "blur(50px)" }} aria-hidden />

        <div className="relative alpha-container pt-16 pb-16 md:pt-24 md:pb-20">
          <div className="grid lg:grid-cols-12 gap-8 items-end">
            <div className="lg:col-span-8 alpha-fade-up">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full alpha-glass-dark text-white text-[11px] uppercase tracking-[0.22em] font-semibold">
                <HomeIcon className="w-3.5 h-3.5" /> Our homes · live vacancies
              </div>
              <h1 className="mt-5 font-display font-medium tracking-tight leading-[0.98] !text-white alpha-press"
                  style={{ fontSize: "clamp(34px, 5.4vw, 68px)" }}>
                Find a home that fits — <span className="alpha-serif-italic alpha-shimmer-text">not the other way around</span>.
              </h1>
              <p className="mt-4 max-w-2xl text-white/85 text-[15.5px] md:text-base alpha-press">
                SIL, SDA, STA &amp; MTA homes across Melbourne &amp; Perth. Filter like real estate — property type, SDA design category, accessibility, state, suburb.
              </p>
            </div>
            <div className="lg:col-span-4 flex lg:justify-end gap-2.5 flex-wrap">
              <Link to="/referrals" className="bg-white text-[color:var(--alpha-purple-700)] rounded-full px-6 py-3.5 font-semibold inline-flex items-center justify-center gap-2 text-sm hover:scale-[1.02] transition-transform" data-testid="vac-hero-refer">
                Start a referral <ArrowRight className="w-4 h-4" />
              </Link>
              <a href="tel:1300380692" onClick={() => trackPhoneClick("1300 380 692", "vacancies-hero")} className="alpha-glass-dark border border-white/30 text-white rounded-full px-6 py-3.5 font-semibold inline-flex items-center justify-center gap-2 text-sm hover:bg-white/15 transition-colors">
                <Phone className="w-4 h-4" /> 1300 380 692
              </a>
            </div>
          </div>
          <div className="mt-7 flex flex-wrap gap-2">
            {[
              { Icon: ShieldCheck, label: "NDIS Registered" },
              { Icon: HomeIcon, label: "SIL · SDA · STA · MTA" },
              { Icon: HomeIcon, label: "4 SDA categories" },
              { Icon: HomeIcon, label: "Melbourne · Perth hubs" },
            ].map((c) => (
              <span key={c.label} className="alpha-glass-dark text-white !py-1.5 !px-3 !text-[11.5px] rounded-full inline-flex items-center gap-1.5 border border-white/15">
                <c.Icon className="w-3.5 h-3.5 text-[color:var(--alpha-green-500)]" /> {c.label}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Mobile filters trigger */}
      <div className="lg:hidden sticky top-[112px] z-30 bg-white/90 backdrop-blur border-b border-[color:var(--alpha-line)]">
        <div className="alpha-container py-3 flex items-center justify-between">
          <div className="text-sm text-[color:var(--alpha-mute)]">{loading ? "Loading…" : `${filteredProps.length + (showApiItems ? items.length : 0)} homes`}</div>
          <Sheet>
            <SheetTrigger asChild>
              <button className="alpha-btn-ghost text-sm" data-testid="mobile-filters-btn"><SlidersHorizontal className="w-4 h-4" /> Filters</button>
            </SheetTrigger>
            <SheetContent side="left" className="w-[320px] overflow-y-auto">
              <div className="py-4">
                <Filters facets={facets} filters={filters} setFilters={setFilters} clear={clear} />
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>

      <section className="alpha-container py-10 grid lg:grid-cols-12 gap-10">
        <aside className="hidden lg:block lg:col-span-3 sticky top-32 self-start">
          <div className="alpha-card p-6">
            <Filters facets={facets} filters={filters} setFilters={setFilters} clear={clear} />
          </div>
        </aside>

        <div className="lg:col-span-9">
          {/* Service-type tabs (Part 12) */}
          <div className="mb-5 flex flex-wrap gap-2" role="tablist" aria-label="Home type" data-testid="vac-tabs">
            {[
              { v: "sil", l: "SIL" },
              { v: "sda", l: "SDA" },
              { v: "all", l: "All homes" },
              { v: "stamta", l: "STA / MTA" },
            ].map((t) => (
              <button
                key={t.v}
                role="tab"
                aria-selected={tab === t.v}
                onClick={() => setTab(t.v)}
                className={`px-4 py-2 rounded-full text-sm font-semibold border transition-colors ${
                  tab === t.v
                    ? "bg-[color:var(--alpha-purple-700)] text-white border-[color:var(--alpha-purple-800)]"
                    : "bg-white text-[color:var(--alpha-ink)] border-[color:var(--alpha-line)] hover:border-[color:var(--alpha-purple-700)] hover:text-[color:var(--alpha-purple-700)]"
                }`}
                data-testid={`vac-tab-${t.v}`}
              >
                {t.l}
              </button>
            ))}
          </div>

          <div className="hidden lg:flex items-center justify-between mb-6">
            <div className="text-sm text-[color:var(--alpha-mute)]" data-testid="vacancy-count">
              {loading ? "Loading…" : (() => {
                const n = filteredProps.length + (showApiItems ? items.length : 0);
                return `${n} home${n === 1 ? "" : "s"} matching your filters`;
              })()}
            </div>
          </div>

          {loading ? (
            <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-6">
              {[...Array(6)].map((_, i) => (
                <div key={`skeleton-${i}`} className="alpha-card overflow-hidden">
                  <div className="h-56 bg-[color:var(--alpha-soft)] animate-pulse" />
                  <div className="p-5 space-y-2">
                    <div className="h-4 w-3/4 bg-[color:var(--alpha-soft)] animate-pulse rounded" />
                    <div className="h-3 w-1/2 bg-[color:var(--alpha-soft)] animate-pulse rounded" />
                  </div>
                </div>
              ))}
            </div>
          ) : filteredProps.length === 0 && (!showApiItems || items.length === 0) ? (
            <div className="space-y-6">
              <div className="alpha-card p-10 text-center" data-testid="no-results">
                <div className="font-display text-2xl font-semibold">No current {tab === "sil" ? "SIL" : tab === "sda" ? "SDA" : ""} vacancies match those requirements.</div>
                <p className="text-[color:var(--alpha-mute)] mt-3 max-w-xl mx-auto">Every enquiry helps us match participants with the right home — even when nothing on this page is a fit today. Here are your options:</p>
                <div className="mt-6 flex flex-wrap gap-3 justify-center">
                  <Link to="/referrals/participant" className="alpha-btn-primary text-sm" data-testid="no-results-refer">
                    Make a SIL referral <ArrowRight className="w-4 h-4" />
                  </Link>
                  <a href="tel:1300380692" onClick={() => trackPhoneClick("1300 380 692", "vacancies-empty")} className="alpha-btn-green text-sm" data-testid="no-results-call">
                    <Phone className="w-4 h-4" /> Speak with intake
                  </a>
                  <button onClick={clear} className="alpha-btn-ghost text-sm" data-testid="no-results-clear">Clear filters</button>
                  <Link to="/locations" className="alpha-btn-ghost text-sm" data-testid="no-results-locations">View nearby areas</Link>
                </div>
              </div>
              <VacancyAlertForm
                defaults={{ state: filters.state || "", support_ratio: filters.support_ratios?.[0] || "", single_storey: filters.accessibility?.includes("single-storey") }}
                testidPrefix="vac-alert-empty"
              />
            </div>
          ) : (
            <>
              <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-6" data-testid="vacancy-grid">
                {filteredProps.map((p) => <PropertyListingCard key={p.slug} property={p} />)}
                {showApiItems && items.map((v) => <VacancyCard key={v.id} vacancy={v} />)}
              </div>
              <div className="mt-10">
                <VacancyAlertForm
                  defaults={{ state: filters.state || "", support_ratio: filters.support_ratios?.[0] || "", single_storey: filters.accessibility?.includes("single-storey") }}
                  testidPrefix="vac-alert"
                />
              </div>
            </>
          )}
        </div>
      </section>

      {/* Step-inside video — support workers & homes */}
      <VideoSection
        src={VIDEOS["support-worker"].url}
        eyebrow="Take a tour · Support workers & homes"
        title="Step inside an Alpha home."
        subtitle={VIDEOS["support-worker"].blurb}
        testid="vac-video-tour"
      />

      {/* Featured Home — cinematic dark band */}
      <FeaturedHome variant="compact" testid="vac-featured-home" />      {/* Final CTA strip */}
      <section className="alpha-section-tight">
        <div className="alpha-container">
          <div className="relative overflow-hidden rounded-[24px] md:rounded-[32px] alpha-gradient p-8 md:p-12 text-white alpha-noise">
            <div className="alpha-orb alpha-orb-green w-[260px] h-[260px] right-[-40px] top-[-40px] opacity-60 hidden md:block" aria-hidden />
            <div className="relative grid md:grid-cols-12 gap-6 items-center">
              <div className="md:col-span-7">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/15 text-[11px] font-semibold uppercase tracking-[0.22em]">
                  <HomeIcon className="w-3.5 h-3.5" /> Didn't find the right fit?
                </div>
                <h2 className="mt-4 font-display text-3xl md:text-[40px] font-medium leading-[1.05] !text-white">
                  New homes open <span className="alpha-serif-italic">monthly</span>.
                </h2>
                <p className="mt-3 text-white/85 text-[15px]">Tell us what you need and we'll match you — before it goes live.</p>
              </div>
              <div className="md:col-span-5 flex flex-col sm:flex-row md:flex-col gap-2.5">
                <Link to="/referrals" className="bg-white text-[color:var(--alpha-purple-700)] rounded-full px-6 py-4 font-semibold text-center hover:bg-[color:var(--alpha-purple-50)] transition inline-flex items-center justify-center gap-2" data-testid="vac-cta-refer">
                  Start a referral <ArrowRight className="w-4 h-4" />
                </Link>
                <a href="tel:1300380692" onClick={() => trackPhoneClick("1300 380 692", "vacancies-cta")} className="border-2 border-white/50 rounded-full px-6 py-4 font-semibold text-center hover:bg-white/10 transition inline-flex items-center justify-center gap-2">
                  <Phone className="w-4 h-4" /> 1300 380 692
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
